import React, { useState, useRef, useEffect } from 'react';
import { Radio, Play, Pause, Volume2, VolumeX, Music, Maximize2, Minimize2, SkipForward, SkipBack, Upload, Trash2, List, X } from 'lucide-react';
import { radioService, RadioTrack } from '../../services/radioService';
import { toastService } from '../../services/toastService';

interface SalesRadioProps {
  orgId: string;
  currentUserId?: string;
  currentUserName?: string;
  isAdmin?: boolean;
}

const SalesRadio: React.FC<SalesRadioProps> = ({ 
  orgId,
  currentUserId,
  currentUserName,
  isAdmin = false
}) => {
  const [tracks, setTracks] = useState<RadioTrack[]>([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load tracks
  useEffect(() => {
    loadTracks();
  }, [orgId]);

  // Debug: Log when showUpload changes
  useEffect(() => {
    console.log('SalesRadio - showUpload changed:', showUpload);
  }, [showUpload]);

  // Debug: Log admin status
  useEffect(() => {
    console.log('SalesRadio - isAdmin:', isAdmin, 'orgId:', orgId, 'userId:', currentUserId);
  }, [isAdmin, orgId, currentUserId]);

  // Setup audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      // Auto-play next track
      if (currentTrackIndex < tracks.length - 1) {
        setCurrentTrackIndex(currentTrackIndex + 1);
      } else {
        setIsPlaying(false);
      }
    };
    
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentTrackIndex, tracks.length]);

  // Auto-play when track changes
  useEffect(() => {
    if (isPlaying && audioRef.current && tracks[currentTrackIndex]) {
      audioRef.current.src = tracks[currentTrackIndex].fileUrl;
      audioRef.current.play().catch(err => console.error('Error playing:', err));
    }
  }, [currentTrackIndex, tracks]);

  const loadTracks = async () => {
    try {
      const loadedTracks = await radioService.getActiveTracks(orgId);
      setTracks(loadedTracks);
    } catch (error) {
      console.error('Error loading tracks:', error);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log('File selected:', file.name, file.type);

    if (!file.type.startsWith('audio/')) {
      alert('Please upload an audio file (MP3, WAV, etc.)');
      toastService.error('Please upload an audio file (MP3, WAV, etc.)');
      return;
    }

    if (!currentUserId || !currentUserName) {
      alert('User information missing');
      toastService.error('User information missing');
      return;
    }

    const title = prompt('Track title:', file.name.replace(/\.[^/.]+$/, '')) || file.name;
    const artist = prompt('Artist name (optional):');

    setUploading(true);
    try {
      console.log('Uploading track...');
      await radioService.uploadTrack(file, {
        title,
        artist,
        orgId,
        uploadedBy: currentUserId,
        uploadedByName: currentUserName
      });
      
      console.log('Track uploaded, reloading...');
      await loadTracks();
      setShowUpload(false);
      toastService.success('Track uploaded successfully!');
    } catch (error) {
      console.error('Error uploading track:', error);
      alert('Failed to upload track: ' + (error as Error).message);
      toastService.error('Failed to upload track');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteTrack = async (track: RadioTrack) => {
    if (!confirm(`Delete "${track.title}"?`)) return;

    try {
      await radioService.deleteTrack(track.id, track.fileName, orgId);
      await loadTracks();
      toastService.success('Track deleted');
      
      // Reset if deleted current track
      if (tracks[currentTrackIndex]?.id === track.id) {
        setCurrentTrackIndex(0);
        setIsPlaying(false);
      }
    } catch (error) {
      console.error('Error deleting track:', error);
      toastService.error('Failed to delete track');
    }
  };

  const togglePlay = () => {
    if (tracks.length === 0) {
      toastService.info('No tracks available. Upload music to get started!');
      return;
    }

    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(err => {
        console.error('Error playing audio:', err);
        toastService.error('Failed to play track');
      });
    }
    setIsPlaying(!isPlaying);
  };

  const nextTrack = () => {
    if (currentTrackIndex < tracks.length - 1) {
      setCurrentTrackIndex(currentTrackIndex + 1);
    } else {
      setCurrentTrackIndex(0); // Loop back to first track
    }
  };

  const previousTrack = () => {
    if (currentTrackIndex > 0) {
      setCurrentTrackIndex(currentTrackIndex - 1);
    } else {
      setCurrentTrackIndex(tracks.length - 1); // Loop to last track
    }
  };

  const selectTrack = (index: number) => {
    setCurrentTrackIndex(index);
    setShowPlaylist(false);
    if (isPlaying && audioRef.current) {
      audioRef.current.play().catch(err => console.error('Error playing:', err));
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    
    audio.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (seconds: number) => {
    if (!isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentTrack = tracks[currentTrackIndex];

  // Render Upload Modal
  const renderUploadModal = () => (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowUpload(false)}>
      <div className="bg-bg-card backdrop-blur-sm rounded-xl lg:rounded-2xl shadow-heavy max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-txt">Upload Track</h3>
          <button
            onClick={() => setShowUpload(false)}
            className="p-2 hover:bg-bg-surface rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="border-2 border-dashed border-strong rounded-lg p-8 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              onChange={handleFileUpload}
              className="hidden"
              disabled={uploading}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center px-4 py-2 bg-accent text-txt-inverted rounded-lg hover:bg-accent disabled:opacity-50 transition-colors"
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Select MP3 File
                </>
              )}
            </button>
            <p className="text-xs text-txt-tertiary mt-3">
              Supported formats: MP3, WAV, OGG
            </p>
          </div>

          <div className="bg-info-soft rounded-lg p-4">
            <p className="text-sm text-info font-medium mb-2">📝 Upload Tips:</p>
            <ul className="text-xs text-info space-y-1">
              <li>• Choose motivational or upbeat music</li>
              <li>• Keep file size under 20MB for best performance</li>
              <li>• You'll be prompted for title and artist</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Playlist Modal
  const renderPlaylistModal = () => (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowPlaylist(false)}>
      <div className="bg-bg-card backdrop-blur-sm rounded-xl lg:rounded-2xl shadow-heavy max-w-md w-full max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-default">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-txt">Playlist ({tracks.length})</h3>
            <button
              onClick={() => setShowPlaylist(false)}
              className="p-2 hover:bg-bg-surface rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="overflow-y-auto max-h-96">
          {tracks.map((track, index) => (
            <div
              key={track.id}
              className={`flex items-center justify-between p-4 hover:bg-bg-surface cursor-pointer transition-colors ${
                index === currentTrackIndex ? 'bg-accent-soft' : ''
              }`}
              onClick={() => selectTrack(index)}
            >
              <div className="flex items-center space-x-3 flex-1">
                <Music className={`w-4 h-4 ${index === currentTrackIndex ? 'text-accent' : 'text-txt-muted'}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${index === currentTrackIndex ? 'text-accent' : 'text-txt'}`}>
                    {track.title}
                  </p>
                  {track.artist && (
                    <p className="text-xs text-txt-tertiary truncate">{track.artist}</p>
                  )}
                </div>
              </div>
              {isAdmin && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteTrack(track);
                  }}
                  className="p-2 hover:bg-danger-soft rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4 text-danger" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (!isExpanded) {
    // Compact View
    return (
      <div className="bg-gradient-to-br from-accent to-accent rounded-xl lg:rounded-2xl shadow-card border-0 p-4 sm:p-6 hover:shadow-card transition-all duration-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-bg-card/20 rounded-lg backdrop-blur-sm">
              <Radio className={`w-5 h-5 text-txt-inverted ${isPlaying ? 'animate-pulse' : ''}`} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-txt-inverted">Sales Radio 🎵</h3>
              <p className="text-xs text-accent">{tracks.length} tracks • {isAdmin ? 'Admin' : 'User'}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {isAdmin ? (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Upload button clicked! Admin:', isAdmin);
                  setShowUpload(true);
                }}
                className="p-2 bg-bg-card/20 hover:bg-bg-card/30 rounded-lg backdrop-blur-sm transition-colors"
                title="Upload Track"
              >
                <Upload className="w-4 h-4 text-txt-inverted" />
              </button>
            ) : (
              <div className="text-xs text-txt-inverted/50 px-2">Admin only</div>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(true);
              }}
              className="p-2 bg-bg-card/20 hover:bg-bg-card/30 rounded-lg backdrop-blur-sm transition-colors"
              title="Expand Player"
            >
              <Maximize2 className="w-5 h-5 text-txt-inverted" />
            </button>
          </div>
        </div>

        {tracks.length > 0 ? (
          <>
            {/* Visual Equalizer Effect */}
            <div className="flex items-center justify-center space-x-1 mb-3">
              {[20, 30, 25, 35, 28, 22, 18].map((height, i) => (
                <div 
                  key={i}
                  className={`w-1 bg-white/${40 + i * 5} rounded-full ${isPlaying ? 'animate-pulse' : ''}`}
                  style={{ 
                    height: `${height}px`, 
                    animationDuration: `${0.6 + i * 0.1}s` 
                  }} 
                />
              ))}
            </div>

            <div className="text-center mb-3">
              <p className="text-sm text-txt-inverted font-medium truncate">
                {currentTrack?.title || 'No track'}
              </p>
              {currentTrack?.artist && (
                <p className="text-xs text-accent mt-1">{currentTrack.artist}</p>
              )}
            </div>

            {/* Quick Controls */}
            <div className="flex items-center justify-center space-x-2">
              <button
                onClick={(e) => { 
                  e.stopPropagation(); 
                  previousTrack(); 
                }}
                className="p-2 bg-bg-card/20 hover:bg-bg-card/30 rounded-lg backdrop-blur-sm transition-colors"
                title="Previous Track"
              >
                <SkipBack className="w-4 h-4 text-txt-inverted" />
              </button>
              <button
                onClick={(e) => { 
                  e.stopPropagation(); 
                  togglePlay(); 
                }}
                className="p-3 bg-white/30 hover:bg-bg-card/40 rounded-lg backdrop-blur-sm transition-colors"
                title={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5 text-txt-inverted" />
                ) : (
                  <Play className="w-5 h-5 text-txt-inverted" />
                )}
              </button>
              <button
                onClick={(e) => { 
                  e.stopPropagation(); 
                  nextTrack(); 
                }}
                className="p-2 bg-bg-card/20 hover:bg-bg-card/30 rounded-lg backdrop-blur-sm transition-colors"
                title="Next Track"
              >
                <SkipForward className="w-4 h-4 text-txt-inverted" />
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <Music className="w-10 h-10 text-txt-inverted/40 mx-auto mb-2" />
            <p className="text-sm text-txt-inverted font-medium">No tracks yet</p>
            <p className="text-xs text-accent mt-1">
              {isAdmin ? 'Upload music to get started' : 'Ask admin to upload tracks'}
            </p>
          </div>
        )}
        
        {currentTrack && (
          <audio ref={audioRef} src={currentTrack.fileUrl} preload="metadata" />
        )}

        {/* Upload Modal - Always available */}
        {showUpload && renderUploadModal()}
        
        {/* Playlist Modal - Always available */}
        {showPlaylist && renderPlaylistModal()}
      </div>
    );
  }

  // Expanded View
  return (
    <div className="relative">
      <div className="bg-gradient-to-br from-accent to-accent rounded-xl lg:rounded-2xl shadow-card border-0 p-4 sm:p-6 hover:shadow-card transition-all duration-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-bg-card/20 rounded-lg backdrop-blur-sm">
              <Music className={`w-6 h-6 text-txt-inverted ${isPlaying ? 'animate-pulse' : ''}`} />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-txt-inverted">Sales Radio</h3>
              <p className="text-sm text-accent truncate">
                {currentTrack ? currentTrack.title : 'No track playing'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {isAdmin && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowUpload(true);
                }}
                className="p-2 bg-bg-card/20 hover:bg-bg-card/30 rounded-lg backdrop-blur-sm transition-colors"
                title="Upload Track"
              >
                <Upload className="w-4 h-4 text-txt-inverted" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowPlaylist(!showPlaylist);
              }}
              className="p-2 bg-bg-card/20 hover:bg-bg-card/30 rounded-lg backdrop-blur-sm transition-colors"
              title="Playlist"
            >
              <List className="w-4 h-4 text-txt-inverted" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(false);
              }}
              className="p-2 bg-bg-card/20 hover:bg-bg-card/30 rounded-lg backdrop-blur-sm transition-colors"
              title="Minimize"
            >
              <Minimize2 className="w-5 h-5 text-txt-inverted" />
            </button>
          </div>
        </div>

        {tracks.length > 0 ? (
          <>
            {/* Current Track Info */}
            {currentTrack?.artist && (
              <div className="text-center mb-3">
                <p className="text-xs text-accent">by {currentTrack.artist}</p>
              </div>
            )}

            {/* Progress Bar */}
            <div className="mb-4">
              <input
                type="range"
                min="0"
                max={duration || 100}
                value={currentTime}
                onChange={handleSeek}
                className="w-full h-2 bg-bg-card/20 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, white ${(currentTime / (duration || 100)) * 100}%, rgba(255,255,255,0.2) ${(currentTime / (duration || 100)) * 100}%)`
                }}
              />
              <div className="flex justify-between text-xs text-accent mt-1">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center space-x-3 mb-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  previousTrack();
                }}
                className="p-2 bg-bg-card/20 hover:bg-bg-card/30 rounded-lg backdrop-blur-sm transition-colors"
                title="Previous"
              >
                <SkipBack className="w-5 h-5 text-txt-inverted" />
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  togglePlay();
                }}
                className="p-4 bg-white/30 hover:bg-bg-card/40 rounded-lg backdrop-blur-sm transition-colors"
                title={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? (
                  <Pause className="w-6 h-6 text-txt-inverted" />
                ) : (
                  <Play className="w-6 h-6 text-txt-inverted" />
                )}
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextTrack();
                }}
                className="p-2 bg-bg-card/20 hover:bg-bg-card/30 rounded-lg backdrop-blur-sm transition-colors"
                title="Next"
              >
                <SkipForward className="w-5 h-5 text-txt-inverted" />
              </button>
            </div>

            {/* Volume Control */}
            <div className="flex items-center space-x-3">
              <button
                onClick={toggleMute}
                className="p-2 bg-bg-card/20 hover:bg-bg-card/30 rounded-lg backdrop-blur-sm transition-colors"
              >
                {isMuted ? (
                  <VolumeX className="w-4 h-4 text-txt-inverted" />
                ) : (
                  <Volume2 className="w-4 h-4 text-txt-inverted" />
                )}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={handleVolumeChange}
                className="flex-1 h-1 bg-bg-card/20 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            {/* Track Info */}
            <div className="mt-4 pt-4 border-t border-white/20">
              <div className="flex items-center justify-between text-txt-inverted text-xs">
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${isPlaying ? 'bg-success/80 animate-pulse' : 'bg-bg-surface-hover'}`} />
                  <span>{isPlaying ? 'Now Playing' : 'Paused'}</span>
                </div>
                <span className="text-accent">Track {currentTrackIndex + 1} of {tracks.length}</span>
              </div>
            </div>

            <audio ref={audioRef} src={currentTrack.fileUrl} preload="metadata" />
          </>
        ) : (
          <div className="text-center py-8">
            <Music className="w-12 h-12 text-txt-inverted/40 mx-auto mb-3" />
            <p className="text-txt-inverted font-medium mb-2">No tracks available</p>
            <p className="text-sm text-accent mb-4">
              {isAdmin ? 'Upload MP3 files to create your Sales Radio playlist' : 'Ask your admin to upload music'}
            </p>
            {isAdmin && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowUpload(true);
                }}
                className="px-4 py-2 bg-bg-card/20 hover:bg-bg-card/30 rounded-lg text-txt-inverted font-medium transition-colors"
              >
                <Upload className="w-4 h-4 inline mr-2" />
                Upload First Track
              </button>
            )}
          </div>
        )}
      </div>

      {/* Render Modals */}
      {showUpload && renderUploadModal()}
      {showPlaylist && renderPlaylistModal()}
    </div>
  );
};

export default SalesRadio;
